import mitraUnenrollmentReportApi from '@/api/mitra-unenrollment-report/mitra-unenrollment-report';
import { normalizeBatchList } from '@/utils/batchIdentity';

const state = {
  courses: [],
  courseDetail: null,
  pagination: {
    total: 0,
    page: 1,
    limit: 10,
    totalPage: 0
  },
  loading: false,
  error: null,
  // Batch related state
  batches: [],
  batchLoading: false,
  batchError: null,
  // Summary statistics
  summary: {
    this_year: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    },
    history: {
      submitted: 0,
      rejected: 0,
      approved: 0,
      completed: 0,
      percentage: 0,
      total: 0
    }
  },
  summaryLoading: false
};

const getters = {
  courses: state => state.courses || [],
  courseDetail: state => state.courseDetail || null,
  pagination: state => state.pagination || { total: 0, page: 1, limit: 10, totalPage: 0 },
  loading: state => state.loading || false,
  error: state => state.error || null,
  // Batch getters
  batches: state => state.batches || [],
  batchLoading: state => state.batchLoading || false,
  batchError: state => state.batchError || null,
  // Summary getters
  summary: state => state.summary || {
    this_year: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
    history: { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
  },
  summaryLoading: state => state.summaryLoading || false
};

const mutations = {
  SET_COURSES(state, courses) {
    state.courses = courses;
  },
  SET_COURSE_DETAIL(state, courseDetail) {
    state.courseDetail = courseDetail;
  },
  SET_PAGINATION(state, pagination) {
    state.pagination = {
      total: pagination.total,
      page: pagination.page,
      limit: pagination.limit,
      totalPage: pagination.totalPage
    };
  },
  SET_LOADING(state, loading) {
    state.loading = loading;
  },
  SET_ERROR(state, error) {
    state.error = error;
  },
  CLEAR_ERROR(state) {
    state.error = null;
  },
  // Batch mutations
  SET_BATCHES(state, batches) {
    state.batches = Array.isArray(batches) ? batches : [];
  },
  SET_BATCH_LOADING(state, loading) {
    state.batchLoading = loading;
  },
  SET_BATCH_ERROR(state, error) {
    state.batchError = error;
  },
  CLEAR_BATCH_ERROR(state) {
    state.batchError = null;
  },
  // Summary mutations
  SET_SUMMARY(state, summary) {
    if (summary) {
      state.summary = {
        this_year: summary.this_year || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 },
        history: summary.history || { submitted: 0, rejected: 0, approved: 0, completed: 0, percentage: 0, total: 0 }
      };
    }
  },
  SET_SUMMARY_LOADING(state, loading) {
    state.summaryLoading = loading;
  }
};

const actions = {
  async getCourses({ commit }, params) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentReportApi.getCourses(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSES', response.data.data.data);
        commit('SET_PAGINATION', {
          total: response.data.data.total || 0,
          page: response.data.data.page || 1,
          limit: response.data.data.limit || 10,
          totalPage: response.data.data.totalPage || 0
        });
        return response.data.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch courses');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  async getCourseDetail({ commit }, id) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentReportApi.getCourseDetail(id);
      
      if (response.data.httpCode === 200) {
        commit('SET_COURSE_DETAIL', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch course detail');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get batches for approval
  async getBatchesApproval({ commit }, payload) {
    commit('SET_BATCH_LOADING', true);
    commit('CLEAR_BATCH_ERROR');
    
    try {
      const courseId = typeof payload === 'object' ? payload.courseId : payload;
      const params = typeof payload === 'object' ? payload.params : undefined;
      const response = await mitraUnenrollmentReportApi.getBatchesApproval(courseId, params);
      
      if (response.data.httpCode === 200) {
        commit('SET_BATCHES', normalizeBatchList(response.data.data));
        return response.data.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch batches');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_BATCH_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_BATCH_LOADING', false);
    }
  },

  // Approve participants
  async approveParticipants({ commit }, { batchId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentReportApi.approveParticipants(batchId, data);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to approve participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Reject participants
  async rejectParticipants({ commit }, { batchId, data }) {
    commit('SET_LOADING', true);
    commit('CLEAR_ERROR');
    
    try {
      const response = await mitraUnenrollmentReportApi.rejectParticipants(batchId, data);
      
      if (response.data.httpCode === 200) {
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to reject participants');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || error.message || 'An error occurred';
      commit('SET_ERROR', errorMessage);
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  // Get summary statistics
  async getSummary({ commit }, params) {
    commit('SET_SUMMARY_LOADING', true);
    
    try {
      const response = await mitraUnenrollmentReportApi.getSummary(params);
      
      if (response.data.httpCode === 200) {
        commit('SET_SUMMARY', response.data.data);
        return response.data;
      } else {
        throw new Error(response.data.message || 'Failed to fetch summary');
      }
    } catch (error) {
      console.error('Error fetching summary:', error);
      throw error;
    } finally {
      commit('SET_SUMMARY_LOADING', false);
    }
  }
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
};
